import React, { memo, useState, useRef, useEffect } from 'react'
import { Col, Form, DatePicker, Row } from 'antd'
import PropTypes from 'prop-types'
import { hexToRGBA, generateFormItem, getDateOfBirthFromID } from '../../../../../lib/utils'
import Templates from '../../../../../Profile/utils/templates'
import dayjs from 'dayjs'

const PatientDetailsSection = memo(({ form, specialFormState, setSpecialFormState, token }) => {
  // Add state to track the date value
  const [dateValue, setDateValue] = useState(null)
  
  // Add useEffect to initialize the date value from the form
  useEffect(() => {
    const formDate = form.getFieldValue('dateOfBirth')
    if (formDate) {
      setDateValue(formDate)
    }
  }, [form])

  // Form item styles for consistent appearance
  const formItemStyle = {
    marginBottom: 16
  }

  // Custom selector component with a completely different approach
  const CustomSelector = ({ options, name, value, onChange }) => {
    const containerRef = useRef(null)
    
    // Handle option selection
    const handleSelect = (val) => {
      // Update the form value
      form.setFieldValue(name, val)
      
      // If there's an onChange handler, call it
      if (onChange) {
        onChange(val)
      }
      
      // Update the visual state directly
      if (containerRef.current) {
        // Get all option elements
        const optionElements = containerRef.current.querySelectorAll('.custom-option')
        
        // Add null check before using forEach
        if (optionElements && optionElements.length > 0) {
          // Reset all options to unselected state
          optionElements.forEach(el => {
            el.style.backgroundColor = '#fff'
            el.style.color = 'rgba(0, 0, 0, 0.88)'
            el.style.borderColor = '#d9d9d9'
          })
          
          // Find the selected option and update its style
          const selectedOption = Array.from(optionElements).find(
            el => el.getAttribute('data-value') === val
          )
          
          if (selectedOption) {
            selectedOption.style.backgroundColor = token.colorPrimary
            selectedOption.style.color = 'white'
            selectedOption.style.borderColor = token.colorPrimary
          }
        }
      }
    }
    
    // Function to reorder race options if needed
    const getOrderedOptions = () => {
      if (name === 'race') {
        // Create a copy of the options array
        const reorderedOptions = [...options]
        
        // Find the indices of "White" and "Other"
        const whiteIndex = reorderedOptions.findIndex(opt => opt.value === 'White')
        const otherIndex = reorderedOptions.findIndex(opt => opt.value === 'Other')
        
        // If both exist and White comes after Other, swap them
        if (whiteIndex !== -1 && otherIndex !== -1 && whiteIndex > otherIndex) {
          // Swap the positions
          const temp = reorderedOptions[whiteIndex]
          reorderedOptions[whiteIndex] = reorderedOptions[otherIndex]
          reorderedOptions[otherIndex] = temp
        }
        
        return reorderedOptions
      }
      
      return options
    }
    
    // Set initial selection on mount and when value changes
    useEffect(() => {
      if (containerRef.current && value) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          // Add null check before accessing querySelectorAll
          if (containerRef.current) {
            const optionElements = containerRef.current.querySelectorAll('.custom-option')
            
            // Add null check before using forEach
            if (optionElements && optionElements.length > 0) {
              // Reset all options
              optionElements.forEach(el => {
                el.style.backgroundColor = '#fff'
                el.style.color = 'rgba(0, 0, 0, 0.88)'
                el.style.borderColor = '#d9d9d9'
              })
              
              // Find and highlight the selected option
              const selectedOption = Array.from(optionElements).find(
                el => el.getAttribute('data-value') === value
              )
              
              if (selectedOption) {
                selectedOption.style.backgroundColor = token.colorPrimary
                selectedOption.style.color = 'white'
                selectedOption.style.borderColor = token.colorPrimary
              }
            }
          }
        }, 0)
      }
    }, [value, token.colorPrimary])
    
    // Common style for all option buttons
    const optionStyle = {
      display: 'inline-block',
      padding: '5px 14px',
      margin: '0 6px 6px 0',
      borderRadius: '20px',
      border: '1px solid #d9d9d9',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.3s',
      whiteSpace: 'nowrap',
      backgroundColor: '#fff',
      color: 'rgba(0, 0, 0, 0.88)'
    }

    return (
      <div ref={containerRef} style={{ display: 'flex', flexWrap: 'wrap' }}>
        {getOrderedOptions().map(option => (
          <div
            key={option.value}
            className="custom-option"
            data-value={option.value}
            style={{
              ...optionStyle,
              backgroundColor: value === option.value ? token.colorPrimary : '#fff',
              color: value === option.value ? 'white' : 'rgba(0, 0, 0, 0.88)',
              borderColor: value === option.value ? token.colorPrimary : '#d9d9d9'
            }}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </div>
        ))}
      </div>
    )
  }

  // Section with border and embedded label
  const SectionWithBorder = ({ title, children, fitContent }) => {
    const containerStyle = {
      position: 'relative', 
      border: `1px solid ${token.colorBorder}`, 
      borderRadius: 8, 
      padding: '24px 16px 8px',
      height: '100%'
    }

    // If fitContent is true, make the container only as wide as its content
    if (fitContent) {
      containerStyle.display = 'inline-block'
      containerStyle.width = 'auto'
    }

    return (
      <div style={{ marginBottom: 16, height: '100%' }}>
        <div style={containerStyle}>
          <div style={{ 
            position: 'absolute', 
            top: -10, 
            left: 16, 
            backgroundColor: 'white', 
            padding: '0 8px'
          }}>
            <span style={{ 
              color: token.colorText, 
              fontSize: '15px'
            }}>{title}</span>
          </div>
          {children}
        </div>
      </div>
    )
  }

  return (
    <>
      {/* First Name and Last Name */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <div style={{ ...formItemStyle }}>
            {generateFormItem(
              Templates.dataModel.firstName,
              form,
              specialFormState,
              setSpecialFormState
            )}
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div style={{ ...formItemStyle }}>
            {generateFormItem(
              Templates.dataModel.lastName,
              form,
              specialFormState,
              setSpecialFormState
            )}
          </div>
        </Col>
      </Row>

      {/* ID Number and Date of Birth */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <div style={{ ...formItemStyle }}>
            {generateFormItem(Templates.dataModel.idNo, form, null, null, (idValue) => {
              let dateOfBirth = getDateOfBirthFromID(idValue)
              if (dateOfBirth) {
                const dateObj = dayjs(dateOfBirth)
                form.setFieldValue('dateOfBirth', dateObj)
                setDateValue(dateObj) // Also update our local state
              }
            })}
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div style={{ ...formItemStyle }}>
            <Form.Item 
              label={<b style={{ fontSize: 15 }}>Date of Birth</b>} 
              name='dateOfBirth'
              style={{ margin: 0 }}
              rules={[{ required: true, message: 'Please select a date of birth' }]}
            >
              <div style={{ width: '240px' }}>
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="YYYY-MM-DD"
                  placeholder="Select date"
                  allowClear={false}
                  value={dateValue} // Directly set the value prop
                  onChange={(date) => {
                    // Update our local state
                    setDateValue(date)
                    
                    // Update the form field
                    form.setFieldValue('dateOfBirth', date)
                    
                    // Store the date in localStorage as a backup
                    if (date && date.isValid()) {
                      const formattedDate = date.format('YYYY-MM-DD')
                      const patientId = form.getFieldValue('_id')
                      if (patientId) {
                        localStorage.setItem(`patient_dob_${patientId}`, formattedDate)
                      }
                    }
                  }}
                />
              </div>
            </Form.Item>
          </div>
        </Col>
      </Row>

      {/* Spacer */}
      <div style={{ marginTop: 24 }}></div>

      {/* Gender and Race sections with proper alignment */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <SectionWithBorder title="Gender">
            <Form.Item name="gender" style={{ margin: 0 }}>
              <CustomSelector 
                options={Templates.dataModel.gender.options} 
                name="gender" 
                value={form.getFieldValue('gender')}
              />
            </Form.Item>
          </SectionWithBorder>
        </Col>
        <Col xs={24} sm={12} style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <SectionWithBorder title="Race" fitContent={true}>
            <Form.Item name="race" style={{ margin: 0 }}>
              <CustomSelector 
                options={Templates.dataModel.race.options} 
                name="race" 
                value={form.getFieldValue('race')}
              />
            </Form.Item>
          </SectionWithBorder>
        </Col>
      </Row>
    </>
  )
})

PatientDetailsSection.propTypes = {
  form: PropTypes.object.isRequired,
  specialFormState: PropTypes.array.isRequired,
  setSpecialFormState: PropTypes.func.isRequired,
  token: PropTypes.object.isRequired
}

PatientDetailsSection.displayName = 'PatientDetailsSection'

export default PatientDetailsSection 